<template>
  <div class="space-y-6">
    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatsCard
        title="Active Elders"
        :value="stats.activeElders"
        :change="12"
        icon="users"
        color="primary"
      />
      <StatsCard
        title="Calls Today"
        :value="stats.callsToday"
        :change="-5"
        icon="phone"
        color="success"
      />
      <StatsCard
        title="Avg Wellness"
        :value="`${stats.avgWellness}%`"
        :change="3"
        icon="heart"
        color="warning"
      />
      <StatsCard
        title="Active Alerts"
        :value="stats.activeAlerts"
        :change="0"
        icon="alert"
        color="danger"
      />
    </div>
    
    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Elder Profiles Overview (2 cols) -->
      <div class="lg:col-span-2">
        <div class="card">
          <div class="card-header flex items-center justify-between">
            <h3 class="text-lg font-semibold">Elder Profiles</h3>
            <RouterLink to="/profiles" class="text-sm text-primary-600 hover:text-primary-700">
              View All →
            </RouterLink>
          </div>
          <div class="card-body p-0">
            <div class="divide-y divide-gray-200">
              <ProfileCard
                v-for="profile in elderProfiles"
                :key="profile.id"
                :profile="profile"
              />
            </div>
          </div>
        </div>
      </div>
      
      <!-- Recent Alerts (1 col) -->
      <div>
        <div class="card">
          <div class="card-header flex items-center justify-between">
            <h3 class="text-lg font-semibold">Recent Alerts</h3>
            <span class="badge badge-danger">{{ alerts.length }} Active</span>
          </div>
          <div class="card-body space-y-3">
            <AlertItem
              v-for="alert in alerts"
              :key="alert.id"
              :alert="alert"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Charts Row -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Wellness Trend Chart -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold">Wellness Trends</h3>
        </div>
        <div class="card-body">
          <WellnessChart :data="wellnessData" />
        </div>
      </div>
      
      <!-- Call Distribution -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold">Call Distribution</h3>
        </div>
        <div class="card-body">
          <CallDistributionChart :data="callDistribution" />
        </div>
      </div>
    </div>
    
    <!-- Today's Schedule -->
    <div class="card">
      <div class="card-header flex items-center justify-between">
        <h3 class="text-lg font-semibold">Today's Schedule</h3>
        <RouterLink to="/schedule" class="text-sm text-primary-600 hover:text-primary-700">
          Full Schedule →
        </RouterLink>
      </div>
      <div class="card-body">
        <ScheduleTimeline :events="todaySchedule" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import StatsCard from '@/components/dashboard/StatsCard.vue'
import ProfileCard from '@/components/dashboard/ProfileCard.vue'
import AlertItem from '@/components/dashboard/AlertItem.vue'
import WellnessChart from '@/components/charts/WellnessChart.vue'
import CallDistributionChart from '@/components/charts/CallDistributionChart.vue'
import ScheduleTimeline from '@/components/dashboard/ScheduleTimeline.vue'
import { useProfileStore } from '@/stores/profiles'
import { useCallStore } from '@/stores/calls'
import { useAlertStore } from '@/stores/alerts'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
const profileStore = useProfileStore()
const callStore = useCallStore()
const alertStore = useAlertStore()

// Stats - computed from real data
const stats = computed(() => ({
  activeElders: profileStore.activeProfiles.length,
  callsToday: callStore.todaysCalls.length,
  avgWellness: calculateAverageWellness(),
  activeAlerts: alertStore.activeAlerts.length
}))

// Use real profiles from store
const elderProfiles = computed(() => profileStore.profiles.slice(0, 5))

// Use real alerts from store
const alerts = computed(() => alertStore.activeAlerts.slice(0, 5))

// Wellness Chart Data - computed from real data
const wellnessData = computed(() => ({
  labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  datasets: [
    {
      label: 'Average Wellness',
      data: calculateWeeklyWellnessData(),
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)'
    }
  ]
}))

// Call Distribution Data - computed from real data
const callDistribution = computed(() => ({
  labels: ['Completed', 'Missed', 'In Progress', 'Scheduled'],
  datasets: [{
    data: calculateCallDistribution(),
    backgroundColor: [
      'rgb(34, 197, 94)',
      'rgb(239, 68, 68)',
      'rgb(251, 191, 36)',
      'rgb(59, 130, 246)'
    ]
  }]
}))

// Today's Schedule - use real data from store
const todaySchedule = computed(() => callStore.todaysCalls)

// Helper functions for calculations
function calculateAverageWellness() {
  const profiles = profileStore.profiles
  if (profiles.length === 0) return 0
  
  // This would normally come from recent call data or wellness scores
  // Return 0 until wellness tracking is implemented
  return 0
}

function calculateWeeklyWellnessData() {
  // This would normally aggregate wellness data from the past week
  // Return empty data until wellness tracking is implemented
  return [0, 0, 0, 0, 0, 0, 0]
}

function calculateCallDistribution() {
  const calls = callStore.calls
  const distribution = [0, 0, 0, 0] // completed, missed, in_progress, scheduled
  
  calls.forEach(call => {
    switch (call.status) {
      case 'completed':
        distribution[0]++
        break
      case 'missed':
        distribution[1]++
        break
      case 'in_progress':
        distribution[2]++
        break
      case 'scheduled':
        distribution[3]++
        break
    }
  })
  
  return distribution
}

onMounted(async () => {
  // Only load data if user is authenticated
  if (authStore.isAuthenticated) {
    try {
      await Promise.allSettled([
        profileStore.fetchProfiles(),
        callStore.fetchTodaysCalls(),
        alertStore.fetchActiveAlerts()
      ])
    } catch (error) {
      console.log('Some dashboard data failed to load:', error)
      // The stores will handle individual errors and show toasts
    }
  }
})
</script>